import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils.js';

// ─── Types ────────────────────────────────────────────────────

export interface ExpandableTableCell {
  /** Cell content. */
  content: React.ReactNode;
  /** Extra className applied to the `<td>` in addition to the shared border class. */
  className?: string;
  /** Stable React key for this cell. Defaults to its column position if omitted. */
  key?: string;
}

export type ExpandableTableRowVariant = 'error' | 'warning' | 'success' | 'neutral';

export interface ExpandableTableRowProps {
  /**
   * Controls the 3px colored left border strip on the first cell.
   * error → red-400 | warning → amber-400 | success → green-500 | neutral → border color
   */
  variant: ExpandableTableRowVariant;
  /** Whether the detail panel is currently expanded. */
  expanded: boolean;
  /** Called when the expand toggle button is clicked. */
  onToggle: () => void;
  /** aria-label for the expand toggle button. */
  toggleAriaLabel?: string;
  /** data-testid for the expand toggle button. */
  toggleTestId?: string;
  /**
   * Data cells rendered before the toggle column.
   * Each entry provides cell content and an optional per-cell `<td>` className.
   * The shared `border-t border-t-border/40` is always added automatically.
   */
  cells: ExpandableTableCell[];
  /** Total number of columns in the outer table (including the toggle column). Used for spacer colSpans. */
  colCount: number;
  /** First row in a group gets a smaller top gutter (h-2). Subsequent rows get h-3. @default false */
  isFirst?: boolean;
  /** Optional hover tint class applied to the main `<tr>` when not expanded. */
  hoverClassName?: string;
  /**
   * The expanded detail row(s) — typically a `<RowPreviewPanel>` or similar `<tr>` component.
   * Rendered immediately after the main `<tr>` when `expanded` is true.
   */
  children?: React.ReactNode;
  /** data-testid applied to the main `<tr>`. */
  testId?: string;
}

// ─── Variant helpers ─────────────────────────────────────────

const STRIP_CLASS: Record<ExpandableTableRowVariant, string> = {
  error: 'border-l-red-400',
  warning: 'border-l-amber-400',
  success: 'border-l-green-500',
  neutral: 'border-l-border',
};

// ─── Component ───────────────────────────────────────────────

export function ExpandableTableRow({
  variant,
  expanded,
  onToggle,
  toggleAriaLabel,
  toggleTestId,
  cells,
  colCount,
  isFirst = false,
  hoverClassName,
  children,
  testId,
}: ExpandableTableRowProps) {
  const sharedBorder = 'border-t border-t-border/40';

  return (
    <React.Fragment>
      {/* Top gutter spacer */}
      <tr aria-hidden="true">
        <td colSpan={colCount} className={cn('p-0 bg-slate-50', isFirst ? 'h-2' : 'h-3')} />
      </tr>

      {/* Main data row */}
      <tr className={cn('bg-white', !expanded && hoverClassName)} data-testid={testId}>
        {cells.map((cell, i) => (
          <td
            key={cell.key ?? `col-${i}`}
            className={cn(
              sharedBorder,
              i === 0 && cn('border-l-[3px]', STRIP_CLASS[variant]),
              cell.className,
            )}
          >
            {cell.content}
          </td>
        ))}

        {/* Expand toggle cell */}
        <td className={cn('px-2 py-2 text-center', sharedBorder)}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={toggleAriaLabel}
            data-testid={toggleTestId}
            className="flex h-6 w-6 items-center justify-center rounded border border-border hover:bg-muted/50 transition-colors mx-auto"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </button>
        </td>
      </tr>

      {/* Detail panel slot */}
      {expanded ? children : null}

      {/* Bottom separator */}
      <tr aria-hidden="true">
        <td colSpan={colCount} className="h-px p-0 bg-border/30" />
      </tr>
    </React.Fragment>
  );
}
